import React, { useMemo, useRef, useState, useCallback, RefObject } from 'react';
import TimelineScene from './TimelineScene';
import { useDragElement } from '../../hooks/useDragElement';
import AddSceneButton from './AddSceneButton';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { updateClipData } from '../../clipUpdateDataUtils';
import { Scenes } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast } from '@/libs/toast/toast';
import { ClipMetadata } from '@/player-config/src/types/clip-types';
import { SizeType } from '@/player-config/src/types/asset-types';
import { Sequence } from '@/player-config/src/types/RemotionPlayerConfigTypes/Sequence';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

export default function TimelineScenes({
  compositeSegment,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  scenes,
  videoSize,
  showBorder,
  playerConfig,
  compositeSegmentDeleteBounds,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  compositionHeight,
  compositionWidth
}: {
  compositeSegment: CompositeSegment;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  scenes: Scenes;
  videoSize: SizeType;
  showBorder: boolean;
  playerConfig: ClipMetadata['player_config'];
  compositeSegmentDeleteBounds: Sequence[];
  newTimelineContainerRef: RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: RefObject<HTMLDivElement>;
  compositionHeight: number;
  compositionWidth: number;
}) {
  const [draggingScene, setDraggingScene] = useState<string | null>(null);
  const [draggingSceneOffset, setDraggingSceneOffset] = useState<number>(0);
  const [isAnySceneMenuOpen, setIsAnySceneMenuOpen] = useState(false);
  const { fps, clipId } = useClipsContext();
  const { logger } = useAppContext();
  const scenesContainerRef = useRef<HTMLDivElement>(null);

  const scenesWithEndTime = useMemo(() => {
    const maxVideoDuration = compositeSegment.maxBounds.toFrame - compositeSegment.maxBounds.fromFrame;
    if (!compositeSegment || !maxVideoDuration) {
      return [];
    }

    const compositeSegmentCurrentBoundsStart = compositeSegment.currentBounds.fromFrame;
    const updatedScenes = scenes.map((scene, index) => {
      return {
        ...scene,
        toFrame:
          Math.max(scene.toFrame - compositeSegmentCurrentBoundsStart - compositeSegmentDragStart * fps, 0) +
          (index === scenes.length - 1 ? compositeSegmentDragEnd * fps : 0),
        fromFrame:
          index === 0
            ? 0
            : Math.max(scene.fromFrame - compositeSegmentCurrentBoundsStart - compositeSegmentDragStart * fps, 0)
      };
    });

    const sceneIndex = updatedScenes.findIndex(scene => scene.id === draggingScene);
    if (sceneIndex !== -1) {
      updatedScenes[sceneIndex].fromFrame += draggingSceneOffset * fps;
      if (updatedScenes[sceneIndex - 1]) {
        updatedScenes[sceneIndex - 1].toFrame += draggingSceneOffset * fps;
      }
    }

    return updatedScenes;
  }, [
    compositeSegment,
    draggingScene,
    compositeSegmentDragStart,
    compositeSegmentDragEnd,
    scenes,
    draggingSceneOffset,
    fps
  ]);

  const onSceneDragEnd = useCallback(
    async (offset: number, sceneId: string) => {
      if (!sceneId) {
        return;
      }
      const scene = scenes.find(scene => scene.id === sceneId);
      if (!scene) {
        return;
      }
      const newStartTimeInFrames = scene.fromFrame + offset * fps;
      try {
        updateClipData({
          actionType: CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SCENE,
          sceneId,
          fromFrame: newStartTimeInFrames,
          compositeId: compositeSegment.id,
          clipId
        });
      } catch (error) {
        showErrorToast("Updating the scene didn't go as planned. Please try again.");
        logger.error(error);
      }
    },
    [scenes, fps, compositeSegment.id, clipId, logger]
  );

  const { onMouseDown } = useDragElement({
    setDraggingElement: setDraggingScene,
    setDraggingElementOffset: setDraggingSceneOffset,
    onDragEnd: onSceneDragEnd,
    validDropZoneRef: newTimelineContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    fps
  });

  return (
    <div className="relative flex h-[30px]" ref={scenesContainerRef}>
      {scenesWithEndTime.map((scene, index) => {
        const previousScene = scenesWithEndTime[index - 1];
        const nextScene = scenesWithEndTime[index + 1];

        return (
          <TimelineScene
            key={scene.id}
            scene={scene}
            scenes={scenes}
            onMouseDown={onMouseDown}
            previousScene={previousScene}
            nextScene={nextScene}
            isDraggingCurrentScene={draggingScene === scene.id}
            isDraggingSomeScene={draggingScene !== null}
            canDeleteScene={scenes.length > 1}
            compositeSegment={compositeSegment}
            videoSize={videoSize}
            showBorder={showBorder}
            playerConfig={playerConfig}
            compositeSegmentDragStart={compositeSegmentDragStart}
            compositeSegmentDeleteBounds={compositeSegmentDeleteBounds}
            onMenuStateChange={setIsAnySceneMenuOpen}
            compositionHeight={compositionHeight}
            compositionWidth={compositionWidth}
            fps={fps}
          />
        );
      })}
      {!isAnySceneMenuOpen && <AddSceneButton compositeSegment={compositeSegment} />}
    </div>
  );
}
